import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PeacockConnect.css';
import { trackPageView, trackCommunityView, trackWhatsAppClick, trackJoinClick, trackFeatureClick } from '../utils/analytics';
import { getCurrentUser, isAdmin, getDaysRemaining } from '../utils/userAuth';
import VerifiedBadge from '../components/VerifiedBadge';

function PeacockConnect() {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  const communities = [
    { id: 'indian', name: 'Indian Students', flag: '🇮🇳', members: 156, color: '#FF9933', whatsapp: 'https://chat.whatsapp.com/indian-global' },
    { id: 'pakistani', name: 'Pakistani Students', flag: '🇵🇰', members: 89, color: '#01411C', whatsapp: 'https://chat.whatsapp.com/pakistani-global' },
    { id: 'brazilian', name: 'Brazilian Students', flag: '🇧🇷', members: 67, color: '#009C3B', whatsapp: 'https://chat.whatsapp.com/brazilian-global' },
    { id: 'mexican', name: 'Mexican Students', flag: '🇲🇽', members: 78, color: '#006847', whatsapp: 'https://chat.whatsapp.com/mexican-global' },
    { id: 'african', name: 'African Students', flag: '🌍', members: 124, color: '#FFD700', whatsapp: 'https://chat.whatsapp.com/african-global' },
    { id: 'chinese', name: 'Chinese Students', flag: '🇨🇳', members: 93, color: '#DE2910', whatsapp: 'https://chat.whatsapp.com/chinese-global' },
    { id: 'korean', name: 'Korean Students', flag: '🇰🇷', members: 45, color: '#003478', whatsapp: 'https://chat.whatsapp.com/korean-global' },
    { id: 'filipino', name: 'Filipino Students', flag: '🇵🇭', members: 72, color: '#0038A8', whatsapp: 'https://chat.whatsapp.com/filipino-global' },
    { id: 'caribbean', name: 'Caribbean Students', flag: '🌴', members: 58, color: '#00CED1', whatsapp: 'https://chat.whatsapp.com/caribbean-global' },
    { id: 'european', name: 'European Students', flag: '🇪🇺', members: 41, color: '#003399', whatsapp: 'https://chat.whatsapp.com/european-global' },
    { id: 'arab', name: 'Arab Students', flag: '🌙', members: 63, color: '#006233', whatsapp: 'https://chat.whatsapp.com/arab-global' },
    { id: 'latinx', name: 'LatinX Students', flag: '🌎', members: 112, color: '#8B4513', whatsapp: 'https://chat.whatsapp.com/latinx-global' },
  ];

  const features = [
    { icon: '🚗', title: 'Rides', desc: 'Share rides with your community', path: '/rides' },
    { icon: '📚', title: 'Tutoring', desc: 'Learn from peer tutors', path: '/services?category=tutoring' },
    { icon: '🛒', title: 'Marketplace', desc: 'Buy & sell within community', path: '/marketplace' },
    { icon: '🤝', title: 'Connect', desc: 'Make friends, build network', path: '/community' },
  ];

  useEffect(() => {
    // Track page view
    trackPageView('peacock_home');

    const timer1 = setTimeout(() => setAnimationPhase(1), 500);
    const timer2 = setTimeout(() => setAnimationPhase(2), 1200);
    const timer3 = setTimeout(() => setAnimationPhase(3), 2000);
    const timer4 = setTimeout(() => setShowSplash(false), 3000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  const openWhatsApp = (communityId, whatsappLink, e) => {
    e.stopPropagation();
    trackWhatsAppClick(communityId);
    window.open(whatsappLink, '_blank');
  };

  const handleCommunityClick = (communityId) => {
    trackCommunityView(communityId);
    navigate(`/community/${communityId}`);
  };

  const handleJoinClick = (communityId, e) => {
    e.stopPropagation();
    trackJoinClick(communityId);
  };

  const handleFeatureClick = (featureName, path) => {
    trackFeatureClick(featureName);
    navigate(path);
  };

  if (showSplash) {
    return (
      <div className="peacock-splash">
        <div className={`splash-content phase-${animationPhase}`}>
          <div className="peacock-logo">
            <span className="peacock-icon">🌍</span>
            <div className="peacock-feathers">
              <div className="feather feather-1"></div>
              <div className="feather feather-2"></div>
              <div className="feather feather-3"></div>
              <div className="feather feather-4"></div>
              <div className="feather feather-5"></div>
            </div>
          </div>
          <h1 className="splash-title">
            <span className="title-peacock">ONTAP</span>
            <span className="title-connect">Global</span>
          </h1>
          <p className="splash-tagline">Connecting International Students</p>
          <div className="splash-flags">
            {communities.slice(0, 6).map((c, i) => (
              <span key={i} className="flag-item" style={{ animationDelay: `${i * 0.1}s` }}>
                {c.flag}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="peacock-page">
      {/* Header */}
      <header className="peacock-header">
        <div className="header-brand">
          <span className="brand-icon">🌍</span>
          <span className="brand-name">ONTAP Global</span>
        </div>
        <div className="header-actions">
          {user ? (
            <>
              {isAdmin(user.email) && (
                <button className="header-btn admin-btn" onClick={() => navigate('/admin')}>
                  Admin
                </button>
              )}
              <div className="user-profile-chip" onClick={() => navigate('/profile')}>
                <span className="user-avatar-small">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
                <span className="user-name-small">
                  {user.name?.split(' ')[0]}
                  {user.isVerified && <VerifiedBadge size="small" />}
                </span>
              </div>
            </>
          ) : (
            <button className="header-btn" onClick={() => navigate('/login')}>
              Sign In
            </button>
          )}
        </div>
      </header>

      {/* Verification Banner for unverified users */}
      {user && !user.isVerified && (
        <div className="verification-banner">
          <span className="banner-icon">⏳</span>
          <span className="banner-text">
            Your account is pending verification. {getDaysRemaining(user.expiresAt)} days remaining.
          </span>
        </div>
      )}

      {/* Hero */}
      <section className="peacock-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span>🎓 100+ Universities | USA & Canada</span>
          </div>
          <h1 className="hero-title">
            Global Students,
            <br />
            <span className="gradient-text">One Community</span>
          </h1>
          <p className="hero-subtitle">
            Connect with fellow international students. Share rides, find tutors,
            trade in the marketplace, and build lasting friendships.
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-value">{communities.length}</span>
              <span className="stat-label">Communities</span>
            </div>
            <div className="stat">
              <span className="stat-value">{communities.reduce((a, c) => a + c.members, 0)}+</span>
              <span className="stat-label">Students</span>
            </div>
            <div className="stat">
              <span className="stat-value">4</span>
              <span className="stat-label">Services</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="peacock-features">
        <h2 className="section-title">What We Offer</h2>
        <div className="features-grid">
          {features.map((feature, i) => (
            <div
              key={i}
              className="feature-card"
              onClick={() => handleFeatureClick(feature.title, feature.path)}
            >
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Communities */}
      <section className="peacock-communities">
        <h2 className="section-title">Find Your Community</h2>
        <p className="section-subtitle">Join your cultural community and connect with fellow students</p>

        <div className="communities-grid">
          {communities.map((community) => (
            <div
              key={community.id}
              className="community-card"
              onClick={() => handleCommunityClick(community.id)}
            >
              <div
                className="community-banner"
                style={{ background: `linear-gradient(135deg, ${community.color}, ${community.color}99)` }}
              >
                <span className="community-flag">{community.flag}</span>
              </div>
              <div className="community-content">
                <h3>{community.name}</h3>
                <span className="member-count">{community.members} members</span>
                <div className="community-actions">
                  <button
                    className="whatsapp-btn"
                    onClick={(e) => openWhatsApp(community.id, community.whatsapp, e)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                  <button className="join-btn" onClick={(e) => handleJoinClick(community.id, e)}>Join</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="peacock-cta">
        <div className="cta-content">
          <h2>Ready to Connect?</h2>
          <p>Join ONTAP Global and connect with international students</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={() => navigate('/register')}>
              Get Started
            </button>
            <button className="cta-secondary" onClick={() => navigate('/community')}>
              Explore Communities
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="peacock-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span>🌍 ONTAP Global</span>
            <p>Connecting International Students</p>
          </div>
          <div className="footer-links">
            <button onClick={() => navigate('/community')}>Communities</button>
            <button onClick={() => navigate('/marketplace')}>Marketplace</button>
            <button onClick={() => navigate('/services')}>Services</button>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 ONTAP Global • USA & Canada Universities</p>
        </div>
      </footer>
    </div>
  );
}

export default PeacockConnect;
